import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, AlertTriangle, X, CloudUpload } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const POLL_MS = 10 * 60 * 1000;

const BackupAlert = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissedOk, setDismissedOk] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/system/backup/status');
      const s = res.data;
      setStatus(s);
      if (s.state === 'success') {
        const key = `backup-ok-toast-${s.today_local}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, '1');
          toast.success(`Daily backup completed — ${s.today_success?.collections_count} collections saved to Bunny CDN`, { duration: 8000 });
        }
        setDismissedOk(!!localStorage.getItem(`backup-ok-dismissed-${s.today_local}`));
      }
    } catch {
      // alert is best-effort; API errors are surfaced elsewhere
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    window.addEventListener('backup-status-changed', load);
    return () => { clearInterval(t); window.removeEventListener('backup-status-changed', load); };
  }, [load]);

  if (!status) return null;

  const problem = status.state === 'failed' || status.state === 'missed' || status.state === 'not_configured'
    || (status.state === 'pending' && status.stale);
  const showOk = status.state === 'success' && !dismissedOk;
  if (!problem && !showOk) return null;

  const dismissOk = () => {
    localStorage.setItem(`backup-ok-dismissed-${status.today_local}`, '1');
    setDismissedOk(true);
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

  let message;
  if (status.state === 'failed') message = `Today's automatic backup FAILED — ${status.today_failed?.error || 'unknown error'}`;
  else if (status.state === 'missed') message = `Today's scheduled backup (${status.schedule_local} ${status.schedule_tz}) did not run.`;
  else if (status.state === 'not_configured') message = 'Automatic backup is OFF — Bunny CDN storage is not configured.';
  else if (problem) message = `No successful backup for ${Math.round(status.hours_since_last_success || 0)} hours (last: ${fmt(status.last_success?.started_at)}).`;

  if (problem) {
    return (
      <div className="bg-red-600 text-white pl-4 pr-16 py-2 flex items-center gap-3 text-sm" data-testid="backup-alert-failed" role="alert">
        {status.state === 'failed' ? <XCircle className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
        <span className="flex-1 font-medium">{message}</span>
        <button
          onClick={() => navigate('/hq/backup')}
          className="inline-flex items-center gap-1 bg-white text-red-700 rounded px-3 py-1 text-xs font-semibold hover:bg-red-50"
          data-testid="backup-alert-action-btn"
        >
          <CloudUpload className="w-3.5 h-3.5" /> Open Backup
        </button>
      </div>
    );
  }

  return (
    <div className="bg-emerald-600 text-white pl-4 pr-16 py-1.5 flex items-center gap-3 text-sm" data-testid="backup-alert-success">
      <CheckCircle2 className="w-4 h-4 shrink-0" />
      <span className="flex-1">
        Today&apos;s automatic backup completed at {fmt(status.today_success?.started_at)} · {status.today_success?.collections_count} collections · {status.today_success?.size_kb} KB on Bunny CDN
      </span>
      <button onClick={dismissOk} className="p-1 rounded hover:bg-emerald-700" aria-label="Dismiss" data-testid="backup-alert-dismiss-btn">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default BackupAlert;
